<template>
  <div>
    <button
      @click="isDateOpen = !isDateOpen"
      style="display: flex; align-items: center; border: 0; background: transparent"
    >
      <!-- eslint-disable-next-line max-len -->
      <img src="@/assets/img/icon/calendar.svg" alt="Date Picker" style="filter: brightness(0) saturate(100%) invert(20%) sepia(93%) saturate(594%) hue-rotate(172deg) brightness(89%) contrast(93%)" />
      <span style="margin-left: 0.5rem; color: #174169; white-space: pre">
        {{ selectedShortcutText }}
      </span>
    </button>
    <date-picker
      v-model="date"
      :min="min"
      :max="max"
      :range="hasRange"
      value-type="YYYY-MM-DD"
      :disabled="disabled"
      :disabled-date="disabledDate"
      :shortcuts="shortcuts.map(({ text, dates }) => ({ text, onClick: () => dates}))"
      :placeholder="placeholder"
      :lang="lang"
      @blur="handleTouched"
      @close="handleTouched"
      @clear="handleClear"
      :open.sync="isDateOpen"
      :editable="!isPhone"
    >
      <!-- mobile only -->
      <template v-slot:sidebar v-if="hasShortcuts">
        <slot name="dateSidebar">
          <img
            src="@/assets/img/close.svg"
            alt="close icon"
            class="close app--hidden app--phone-shown"
            @click="closeDatePicker"
          />
        </slot>
      </template>
      <template v-else v-slot:header>
        <slot name="dateDeader">
          <img
            src="@/assets/img/close.svg"
            alt="close icon"
            class="close app--hidden app--phone-shown"
            @click="closeDatePicker"
          />
        </slot>
      </template>
      <!-- The End of mobile only -->
      <template v-slot:icon-calendar>
        <slot name="icon">
          <img src="@/assets/img/icon/calendar.svg" alt="Date Picker" />
        </slot>
      </template>
    </date-picker>
    <div class="mask" v-if="!disabled && (isDateOpen || isTimeOpen)"></div>
  </div>
</template>

<script>
import { scheduled, asyncScheduler } from 'rxjs';
import {
  tap, map, filter, startWith, distinctUntilChanged, mergeAll,
} from 'rxjs/operators';
import DatePicker from 'vue2-datepicker';
import {
  getMonthStartDate,
  getMonthEndDate,
  getLastMonthStartDate,
  getLastMonthEndDate,
  getWeekStartDate,
  getWeekEndDate,
  addDays,
} from '@/helpers/date';
import 'vue2-datepicker/locale/zh-tw';
import 'vue2-datepicker/locale/en';

const now = new Date();
const nowYear = now.getFullYear();

const defaultShortcuts = [
  {
    text: '今日',
    dates: [now, now],
  },
  {
    text: '本週',
    dates: [getWeekStartDate(), getWeekEndDate()],
  },
  {
    text: '本月',
    dates: [getMonthStartDate(), getMonthEndDate()],
  },
  {
    text: '上月',
    dates: [getLastMonthStartDate(), getLastMonthEndDate()],
  },
  {
    text: '30日前',
    dates: [addDays(now, -30), now],
  },
  {
    text: '90日前',
    dates: [addDays(now, -90), now],
  },
  {
    text: '本年度',
    dates: [new Date(`${nowYear}-01-01`), new Date(`${nowYear}-12-31`)],
  },
  {
    text: '開業至今',
    dates: [new Date('2021-01-01'), now],
  },
];

export default {
  components: {
    DatePicker,
  },
  props: {
    value: [String, Array],
    label: String,
    disabled: Boolean,
    hasTime: Boolean,
    min: {
      type: Date,
      default: null,
    },
    max: {
      type: Date,
      default: null,
    },
    use12h: {
      type: Boolean,
      default: true,
    },
    hasRange: {
      type: Boolean,
      default: false,
    },
    // If hasRange is true,
    // the returned value in onClick function inside shortcuts object must be [Date, Date]
    customShortcuts: Array,
    hasShortcuts: Boolean,
    placeholder: {
      type: String,
      default: 'YYYY-MM-DD',
    },
    defaultTime: String,
  },
  data: () => ({
    inputVal: null,
    date: null,
    time: '00:00:00',
    isDateOpen: false,
    isTimeOpen: false,
    lang: window.navigator.language === 'en' ? 'en' : 'zh-tw',
    isPhone: window.innerWidth <= 600,
  }),
  computed: {
    shortcuts() {
      if (this.customShortcuts !== undefined && this.customShortcuts !== null) {
        return this.customShortcuts;
      }
      if (this.hasShortcuts) {
        return defaultShortcuts;
      }
      return null;
    },
    selectedShortcutText() {
      if (this.date === undefined || this.date === null) {
        return '所有';
      }
      const idx = this.shortcuts.findIndex(
        ({ dates }) => this.date.length === dates.length
          && this.date.every(
            // check if start and end dates match using format "YYYY-MM-DD"
            (_, i) => {
              const yearString = dates[i].getFullYear();
              const monthString = (dates[i].getMonth() + 1).toString().padStart(2, '0');
              const dateString = dates[i].getDate().toString().padStart(2, '0');
              return this.date[i] === `${yearString}-${monthString}-${dateString}`;
            },
          ),
      );
      return idx !== -1 ? this.shortcuts[idx].text : '自訂';
    },
  },
  watch: {
    inputVal(val) {
      this.$emit('input', val);
    },
  },
  subscriptions() {
    return {
      value$: this.$watchAsObservable('value').pipe(
        startWith(1),
        filter(() => {
          if (!this.value) {
            if (this.hasTime) {
              this.time = null;
            }
            this.date = null;
          }
          return !!this.value;
        }),
        tap(() => {
          if (this.hasTime && !this.hasRange) {
            const [date, time] = this.value.split(' ');
            this.date = date;
            this.time = time || '00:00:00';
          } else {
            this.date = this.value;
          }
        }),
      ),
      datetime$: scheduled(
        [this.$watchAsObservable('date'), this.$watchAsObservable('time')],
        asyncScheduler,
      ).pipe(
        mergeAll(),
        map(() => {
          if (!this.hasTime || !this.date) return this.date;
          return `${this.date} ${this.time || this.defaultTime || '00:00:00'}`;
        }),
        distinctUntilChanged(),
        tap((datetime) => {
          this.inputVal = datetime;
        }),
      ),
    };
  },
  methods: {
    handleTouched() {
      this.$emit('touched');
    },
    handleChange(value, type) {
      if (type === 'ampm') {
        this.isTimeOpen = false;
      }
    },
    handleClear() {
      this.inputVal = '';
    },
    closeDatePicker() {
      this.isDateOpen = false;
    },
    closeTimePicker() {
      this.isTimeOpen = false;
    },
    disabledDate(date) {
      if (this.max && this.min) {
        return date < this.min || date > this.max;
      }
      if (this.min) {
        return date < this.min;
      }
      if (this.max) {
        return date > this.max;
      }
      return null;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/datepicker";
.mx-datepicker-header {
  border-bottom: none;
  padding-bottom: 0;
}

@include respond(phone) {
  .mx-datepicker-popup {
    top: 50% !important;
    left: 0 !important;
    width: 100% !important;
    position: fixed !important;
    transform: translateY(-50%);
  }
  .mx-calendar,
  .mx-time {
    width: 100%;
  }
  .mx-datepicker-sidebar + .mx-datepicker-content {
    margin-top: 1.1rem;
    margin-bottom: 1.1rem;
  }
  .mx-calendar + .mx-calendar {
    border: none;
  }
}
</style>

<style scoped lang="scss">
.mx-datepicker {
  display: none;
}

.close {
  margin: 3px 0;
  float: none;
}

::v-deep {
  @include respond(phone) {

    .mask {
        position: fixed;
        top: 0;
        right: 0;
        height: 100vh;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 111;
    }
  }
}
</style>
